<template>
    <v-overlay :model-value="loading" class="align-center justify-center" persistent>
        <v-progress-circular color="amber" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="blog-card m-2">
        <!-- If admin, show icons -->
        <div class="icons position-relative">
            <!-- Archive -->
            <v-dialog v-model="archiveDialog" transition="dialog-top-transition" width="640" persistent>
                <template v-slot:activator="{ props }">
                    <div class="icon position-absolute top-0 end-0 archive-icon" v-bind="props">
                        <font-awesome-icon icon="fa-solid fa-folder-open" class="archive" />
                    </div>
                </template>
                <template v-slot:default="{ isActive }">
                    <v-card>
                        <v-toolbar :color="'#2F437A'">
                            <v-toolbar-title class="text-white">Archive Event</v-toolbar-title>
                            <v-toolbar-items>
                                <v-btn @click="
                                    isActive.value = false;
                                resetArchiveForm();
                                ">
                                    <font-awesome-icon icon="fa-solid fa-xmark" class="fa-fw text-white" />
                                </v-btn>
                            </v-toolbar-items>
                        </v-toolbar>
                        <v-card-text>
                            <!-- Set novalidate because of custom validations -->
                            <form novalidate>
                                <div class="mb-3">
                                    <label for="eventArchiveStatus" class="form-label">Status</label>
                                    <select class="form-select" id="eventArchiveStatus"
                                        v-model="archiveFormData.eventArchiveStatus"
                                        @blur="archiveForm$.eventArchiveStatus.$touch">
                                        <option value="" selected disabled hidden>
                                            Choose here
                                        </option>
                                        <option value="Completed">Completed</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                    <div v-if="archiveForm$.eventArchiveStatus.$errors[0]"
                                        class="alert alert-danger m-0 p-1">
                                        <em>
                                            {{
                                                archiveForm$.eventArchiveStatus.$errors[0].$message
                                            }}</em>
                                    </div>
                                </div>
                            </form>
                        </v-card-text>
                        <v-card-actions class="justify-end">
                            <v-btn variant="text" @click="submitArchiveFormData">Save</v-btn>
                        </v-card-actions>
                    </v-card>
                </template>
            </v-dialog>

            <!-- Edit -->
            <div @click="setupImage" class="icon position-absolute top-0 end-0 edit-icon">
                <font-awesome-icon icon="fa-solid fa-pen-to-square" class="edit" />
            </div>
            <v-dialog v-model="editDialog" transition="dialog-top-transition" width="640" persistent>
                <template v-slot:default="{ isActive }">
                    <v-card>
                        <v-toolbar :color="'#2F437A'">
                            <v-toolbar-title class="text-white">Edit Event</v-toolbar-title>
                            <v-toolbar-items>
                                <v-btn @click="
                                    isActive.value = false;
                                resetUpdateForm();
                                ">
                                    <font-awesome-icon icon="fa-solid fa-xmark" class="fa-fw text-white" />
                                </v-btn>
                            </v-toolbar-items>
                        </v-toolbar>
                        <v-card-text>
                            <!-- Set novalidate because of custom validations -->
                            <form novalidate>
                                <!-- No image uploaded -->
                                <div class="mb-3" v-if="!uploadedEventPhotoUrl">
                                    <label class="form-label">Event Photo</label>
                                    <label for="eventPhoto" class="form-control btn btn-primary">Upload</label>
                                    <input type="file" @change="
                                        onFileChange($event);
                                    previewPhotoUrl($event);
                                    " class="form-control d-none" id="eventPhoto" accept="image/*" />
                                    <div v-for="error in updateForm$.eventPhoto.$errors" :key="error.$uid"
                                        class="alert alert-danger p-1">
                                        <em> {{ error.$message }} </em>
                                    </div>
                                </div>

                                <!-- Has image -->
                                <div class="mb-3" v-else>
                                    <label class="form-label">Event Photo</label>
                                    <br />
                                    <a class="form-text" :href="uploadedEventPhotoUrl" target="_blank">
                                        {{ updateFormData.eventPhotoTitle }}
                                    </a>
                                    <!-- Delete photo -->
                                    <button type="button" class="btn btn-tertiary">
                                        <font-awesome-icon icon="fa-solid fa-xmark" class="fa-fw" @click="
                                            deleteEventPhotoUrl();
                                        deleteFile();
                                        " />
                                    </button>
                                    <div v-for="error in updateForm$.eventPhotoTitle.$errors" :key="error.$uid"
                                        class="alert alert-danger p-1">
                                        <em> {{ error.$message }} </em>
                                    </div>
                                </div>

                                <!-- Event title -->
                                <div class="mb-3">
                                    <label for="eventTitle" class="form-label">Title</label>
                                    <input type="text" class="form-control" id="eventTitle"
                                        v-model="updateFormData.eventTitle" @input="updateForm$.eventTitle.$touch" />
                                    <div v-if="updateForm$.eventTitle.$errors[0]" class="alert alert-danger m-0 p-1">
                                        <em> {{ updateForm$.eventTitle.$errors[0].$message }}</em>
                                    </div>
                                </div>

                                <!-- Event category -->
                                <div class="mb-3">
                                    <label for="eventCategory" class="form-label">Category</label>
                                    <input type="text" class="form-control" id="eventCategory"
                                        v-model="updateFormData.eventCategory" @input="updateForm$.eventCategory.$touch" />
                                    <div v-if="updateForm$.eventCategory.$errors[0]" class="alert alert-danger m-0 p-1">
                                        <em>
                                            {{ updateForm$.eventCategory.$errors[0].$message }}</em>
                                    </div>
                                </div>

                                <!-- Event Schedule -->
                                <div class="mb-3">
                                    <label for="eventTime" class="form-label">Schedule</label>
                                    <input type="datetime-local" class="form-control" id="eventTime"
                                        v-model="updateFormData.eventTime" @input="updateForm$.eventTime.$touch" />
                                    <div v-if="updateForm$.eventTime.$errors[0]" class="alert alert-danger m-0 p-1">
                                        <em> {{ updateForm$.eventTime.$errors[0].$message }}</em>
                                    </div>
                                </div>

                                <!-- Possible event hosts -->
                                <div class="mb-3">
                                    <label for="eventHost" class="form-label">Host</label>
                                    <select v-if="updateFormData.eventHost[0] === ''" class="form-select" id="eventHost"
                                        v-model="updateFormData.eventHost">
                                        <option v-if="eventVolunteers.length == 0" :value="['', '']" selected disabled
                                            hidden>
                                            No approved volunteers
                                        </option>
                                        <option v-else :value="['', '']" selected disabled hidden>
                                            Choose here
                                        </option>
                                        <option v-for="volunteer in eventVolunteers" :key="volunteer.id"
                                            :value="[volunteer.id, volunteer.Name]">
                                            Name: {{ volunteer.Name }} | Email: {{ volunteer.Email }}
                                        </option>
                                    </select>

                                    <select v-if="updateFormData.eventHost[0] !== ''" class="form-select" id="eventHost"
                                        v-model="updateFormData.eventHost" @change="changeVisiblity">
                                        <option :value="['', '']">None</option>
                                        <option v-if="!eventVolunteers.some(
                                            (element) =>
                                                element.id === updateFormData.eventHost[0]
                                        )
                                            " :value="updateFormData.eventHost" selected disabled hidden>
                                            Selected volunteer was archived
                                        </option>
                                        <option v-else v-for="volunteer in eventVolunteers" :key="volunteer.id" :selected="volunteer.id === updateFormData.eventHost ? true : false
                                            " :value="[volunteer.id, volunteer.Name]">
                                            Name: {{ volunteer.Name }} | Email: {{ volunteer.Email }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Event Platform -->
                                <div class="mb-3">
                                    <label for="eventPlatform" class="form-label">Platform</label>
                                    <select class="form-select" id="eventPlatform" v-model="updateFormData.eventPlatform">
                                        <option value="" selected disabled hidden>
                                            Choose here
                                        </option>
                                        <option value="Facebook">Facebook</option>
                                        <option value="Zoom">Zoom</option>
                                        <option value="MS Teams">MS Teams</option>
                                        <option value="Kumu">Kumu</option>
                                        <option value="YouTube">YouTube</option>
                                        <option value="Google Meet">Google Meet</option>
                                        <option value="Discord">Discord</option>
                                        <option value="Twitch">Twitch</option>
                                        <option value="Kick">Kick</option>
                                    </select>
                                    <div v-if="updateForm$.eventPlatform.$errors[0]" class="alert alert-danger m-0 p-1">
                                        <em>
                                            {{ updateForm$.eventPlatform.$errors[0].$message }}</em>
                                    </div>
                                </div>

                                <!-- Event Description -->
                                <div class="mb-3">
                                    <label for="eventDescription" class="form-label">Description</label>
                                    <textarea class="form-control" id="eventDescription"
                                        v-model="updateFormData.eventDescription" placeholder="Enter a description"
                                        @input="updateForm$.eventDescription.$touch" />

                                    <div v-if="updateForm$.eventDescription.$errors[0]" class="alert alert-danger m-0 p-1">
                                        <em>
                                            {{ updateForm$.eventDescription.$errors[0].$message }}</em>
                                    </div>
                                </div>

                                <!-- Event Visibility -->
                                <div class="mb-3">
                                    <label for="eventIsOpen" class="form-label">Open Event</label>
                                    <select class="form-select" id="eventIsOpen" v-model="updateFormData.eventIsOpen"
                                        v-if="updateFormData.eventHost[0] !== ''">
                                        <option :value="true">Yes</option>
                                        <option :value="false">No</option>
                                    </select>
                                    <select class="form-select" id="eventIsOpen" v-model="updateFormData.eventIsOpen"
                                        v-else>
                                        <option :value="false" selected>No</option>
                                    </select>
                                    <div id="openHelpBlock" class="form-text">
                                        Note: Opening an event will place it inside the 'Upcoming
                                        Events' section of the home page. Closing an event will
                                        place it inside the 'Volunteer Opportunities' section of the
                                        home page. Events where the host is not set cannot be
                                        opened.
                                    </div>
                                </div>
                            </form>
                        </v-card-text>
                        <v-card-actions class="justify-end">
                            <v-btn variant="text" @click="submitUpdateFormData">Save</v-btn>
                        </v-card-actions>
                    </v-card>
                </template>
            </v-dialog>

            <!-- Delete -->
            <div @click="confirmDelete" class="icon position-absolute top-0 end-0 delete-icon">
                <i class="fa-solid fa-trash-can delete"></i>
                <font-awesome-icon icon="fa-solid fa-trash-can" class="delete" />
            </div>

            <v-dialog v-model="deleteDialog" transition="dialog-top-transition" width="640" persistent>
                <template v-slot:default="{ isActive }">
                    <v-card>
                        <v-toolbar :color="'#2F437A'">
                            <v-toolbar-title class="text-white">Delete Event</v-toolbar-title>
                            <v-toolbar-items>
                                <v-btn @click="
                                    isActive.value = false;
                                resetUpdateForm();
                                ">
                                    <font-awesome-icon icon="fa-solid fa-xmark" class="fa-fw text-white" />
                                </v-btn>
                            </v-toolbar-items>
                        </v-toolbar>
                        <v-card-text>
                            Are you sure you want to delete this event?
                        </v-card-text>
                        <v-card-actions class="justify-end">
                            <v-spacer></v-spacer>
                            <v-btn variant="text" @click="isActive.value = false"> No </v-btn>
                            <v-btn variant="text" @click="deleteEvent"> Yes </v-btn>
                        </v-card-actions>
                    </v-card>
                </template>
            </v-dialog>
        </div>
        <img v-if="event.ThumbnailUrl" :src="event.ThumbnailUrl" class="img-thumbnail" />
        <img v-else :src="emptyImageUrl" class="img-thumbnail" />
        <router-link :to="`/events/view-beneficiaries-volunteers/${event.id}`"
            class="checkButton link-offset-2 link-underline link-underline-opacity-0">
            View Attendees
        </router-link>

        <div class="card-body">
            <h6 v-if="event.CreationTimestamp">
                Created on:
                {{
                    new Date(event.CreationTimestamp.toDate()).toLocaleString("en-us", {
                        dateStyle: "long",
                    })
                }}
            </h6>
            <h6 v-if="event.CreatedBy">Created by: {{ event.CreatedBy }}</h6>
            <h6 v-if="event.ModifiedTimestamp">
                Modified on
                {{
                    new Date(event.ModifiedTimestamp.toDate()).toLocaleString("en-us", {
                        dateStyle: "long",
                    })
                }}
            </h6>
            <h6 v-if="event.ModifiedBy">Modified by: {{ event.ModifiedBy }}</h6>
        </div>
    </div>
</template>

<script>
import {
    doc,
    deleteDoc,
    query,
    collection,
    where,
    orderBy,
    getDocs,
    Timestamp,
    serverTimestamp,
    updateDoc,
    writeBatch,
} from "firebase/firestore";
import moment from "moment";
import { db, uploadToStorage } from "@/firebase";
import store from "@/store";
import { ref, reactive, watch, computed } from "vue";
import { useStore } from "vuex";
import {
    deleteObject,
    getStorage,
    ref as storageRef,
    uploadBytesResumable,
} from "@firebase/storage";
import useVuelidate from "@vuelidate/core";
import { required, helpers, maxLength } from "@vuelidate/validators";
const { withAsync } = helpers;
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { v4 as uuidv4 } from "uuid";

export default {
    data() {
        return {
            uploadedEventPhoto: null,
            uploadedEventPhotoUrl: null,
        };
    },
    props: ["event"],
    setup(props) {
        const storage = getStorage();
        const user = computed(() => useStore().getters["auth/user"]);
        const email = user.value.email; // For modified by field

        // Form data for update and edit
        const updateFormData = reactive({
            eventPhoto: props.event.ThumbnailUrl,
            eventPhotoTitle: props.event.ThumbnailUrlFileName,
            eventTitle: props.event.Name,
            eventCategory: props.event.Category,
            eventTime: moment(new Date(props.event.StartTime.seconds * 1000)).format(
                "YYYY-MM-DDTHH:mm"
            ),
            eventHost: [props.event.MainHost, props.event.MainHostName],
            eventPlatform: props.event.Platform,
            eventDescription: props.event.Description,
            eventIsOpen: props.event.IsOpen,
        });
        const archiveFormData = reactive({
            eventArchiveStatus: props.event.ArchiveStatus,
        });

        // Watch for updates
        watch(
            () => props.event,
            (newVal) => {
                updateFormData.eventPhoto = newVal.ThumbnailUrl;
                updateFormData.eventPhotoTitle = newVal.ThumbnailUrlFileName;
                updateFormData.eventTitle = newVal.Name;
                updateFormData.eventCategory = newVal.Category;
                updateFormData.eventTime = newVal.StartTime
                    ? moment(new Date(props.event.StartTime.seconds * 1000)).format(
                        "YYYY-MM-DDTHH:mm"
                    )
                    : null;
                updateFormData.eventHost = [newVal.MainHost, newVal.MainHostName];
                updateFormData.eventPlatform = newVal.Platform;
                updateFormData.eventDescription = newVal.Description;
                updateFormData.eventIsOpen = newVal.IsOpen;
                archiveFormData.eventArchiveStatus = newVal.ArchiveStatus;
            },
            { deep: true }
        );

        // Custom rule for category
        const categoryExists = withAsync(async (value) => {
            const events = [];

            const q = query(
                collection(db, "Events"),
                where("__name__", "!=", props.event.id),
                where("IsArchived", "==", false)
            );

            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
                events.push(doc.data().Category.toLowerCase());
            });

            return !events.includes(value.toLowerCase());
        });

        const isAcceptedImageFileExtension = (value) => {
            const acceptedExtensions = [
                "xbm",
                "tif",
                "jfif",
                "ico",
                "tiff",
                "gif",
                "svg",
                "jpeg",
                "svgz",
                "jpg",
                "webp",
                "png",
                "bmp",
                "pjp",
                "apng",
                "pjpeg",
                "avif",
            ];
            const fileExtension = value.split(".").pop();
            return acceptedExtensions.includes(fileExtension);
        };

        // Vuelidate rules
        const updateFormRules = {
            eventPhoto: {
                required: helpers.withMessage("An image is required", required),
            },
            eventPhotoTitle: {
                isAcceptedFileExtension: helpers.withMessage(
                    "The file must be an image",
                    isAcceptedImageFileExtension
                ),
                maxLength: helpers.withMessage(
                    "The maximum file title length allowed is 150",
                    maxLength(150)
                ),
            },
            eventTitle: { required, maxLength: maxLength(100) },
            eventCategory: {
                required,
                categoryExists: helpers.withMessage(
                    "The category already exists",
                    categoryExists
                ),
                maxLength: maxLength(25),
            },
            eventPlatform: { required },
            eventTime: { required },
            eventDescription: { required, maxLength: maxLength(500) },
        };

        const archiveFormRules = {
            eventArchiveStatus: { required },
        };

        // Vuelidate init
        const updateForm$ = useVuelidate(updateFormRules, updateFormData);
        const archiveForm$ = useVuelidate(archiveFormRules, archiveFormData);

        // Dialog handling
        const editDialog = ref(false);
        const archiveDialog = ref(false);
        const deleteDialog = ref(false);

        // Overlay handling
        const loading = ref(false);

        // If x mark is clicked, discard changes
        const resetUpdateForm = () => {
            updateFormData.eventPhoto = props.event.ThumbnailUrl;
            updateFormData.eventPhotoTitle = props.event.ThumbnailUrlFileName;
            updateFormData.eventTitle = props.event.Name;
            updateFormData.eventCategory = props.event.Category;
            updateFormData.eventTime = moment(
                new Date(props.event.StartTime.seconds * 1000)
            ).format("YYYY-MM-DDTHH:mm");
            updateFormData.eventHost = [
                props.event.MainHost,
                props.event.MainHostName,
            ];
            updateFormData.eventPlatform = props.event.Platform;
            updateFormData.eventDescription = props.event.Description;
            updateFormData.eventIsOpen = props.event.IsOpen;

            // Reset errors
            updateForm$.value.$reset();
        };

        const resetArchiveForm = () => {
            archiveFormData.eventArchiveStatus = props.event.ArchiveStatus;

            // Reset errors
            archiveForm$.value.$reset();
        };

        // File validation and event listeners
        const onFileChange = (event) => {
            updateFormData.eventPhoto = event.target.files[0];
            updateFormData.eventPhotoTitle = event.target.files[0]
                ? event.target.files[0].name
                : null;
        };
        const deleteFile = () => {
            updateFormData.eventPhoto = null;
            updateFormData.eventPhotoTitle = null;
        };

        // Submit
        const submitUpdateFormData = async () => {
            // Overlay handling
            loading.value = true;

            // Validate category again because it is sensitive to changes
            const eventCategoryForceValidate = updateForm$.value.eventCategory.$model;
            updateForm$.value.eventCategory.$model = null;
            updateForm$.value.eventCategory.$model = eventCategoryForceValidate;

            const result = await updateForm$.value.$validate();
            if (result) {
                updateForm$.value.$reset();

                let eventPhotoDownloadUrl = null;

                // If new image is uploaded, it is of instance file.
                if (!(updateFormData.eventPhoto instanceof File)) {
                    // If no new image is uploaded, use the previous image URL
                    eventPhotoDownloadUrl = updateFormData.eventPhoto;
                } else {
                    // If new image is uploaded, upload it to storage
                    const storageEventPhotoRef = storageRef(
                        storage,
                        "Event Photos/" + uuidv4() + updateFormData.eventPhoto.name
                    );
                    const eventPhotoUploadTask = uploadBytesResumable(
                        storageEventPhotoRef,
                        updateFormData.eventPhoto
                    );
                    eventPhotoDownloadUrl = await uploadToStorage(eventPhotoUploadTask);

                    // Delete previous photo
                    await deleteObject(storageRef(storage, props.event.ThumbnailUrl))
                        .then(() => {
                            console.log("Previous event photo successfully deleted");
                        })
                        .catch((error) => {
                            console.log(error);
                            console.log("Unable to delete previous event photo");
                        });
                }

                await updateDoc(doc(db, "Events", props.event.id), {
                    Name: updateFormData.eventTitle,
                    ThumbnailUrl: eventPhotoDownloadUrl,
                    ThumbnailUrlFileName: updateFormData.eventPhotoTitle,
                    Category: updateFormData.eventCategory,
                    StartTime: Timestamp.fromDate(new Date(updateFormData.eventTime)),
                    MainHost: updateFormData.eventHost[0],
                    MainHostName: updateFormData.eventHost[1],
                    IsOpen: updateFormData.eventIsOpen,
                    Platform: updateFormData.eventPlatform,
                    Description: updateFormData.eventDescription,
                    ModifiedTimestamp: serverTimestamp(),
                    ModifiedBy: email,
                });

                toast.success("Event successfully updated", {
                    autoClose: 2000,
                });

                editDialog.value = false;
            } else {
                toast.error("Unable to save details");
            }

            loading.value = false;
        };

        const submitArchiveFormData = async () => {
            loading.value = true;

            const result = await archiveForm$.value.$validate();
            if (result) {
                archiveForm$.value.$reset();

                await updateDoc(doc(db, "Events", props.event.id), {
                    ArchiveStatus: archiveFormData.eventArchiveStatus,
                    IsArchived: true,
                    IsOpen: false,
                    ModifiedTimestamp: serverTimestamp(),
                    ModifiedBy: email,
                });

                // Get all event registrations and volunteers for this event
                const eventRegistrationsCollection = collection(
                    db,
                    "Event Registrations"
                );
                const volunteersCollection = collection(db, "Volunteer Applications");

                const eventsQuery = query(
                    eventRegistrationsCollection,
                    where("EventRegisteredId", "==", props.event.id),
                    where("IsArchived", "==", false)
                );
                const volunteersQuery = query(
                    volunteersCollection,
                    where("EventAppliedId", "==", props.event.id),
                    where("IsArchived", "==", false)
                );

                const eventsQuerySnapshot = await getDocs(eventsQuery);
                const volunteersQuerySnapshot = await getDocs(volunteersQuery);

                const batch = writeBatch(db);

                // Archive all event registrations and volunteers associated with this event
                eventsQuerySnapshot.forEach((doc) => {
                    batch.update(doc.ref, {
                        IsArchived: true,
                    });
                });

                volunteersQuerySnapshot.forEach((doc) => {
                    batch.update(doc.ref, {
                        IsArchived: true,
                    });
                });

                await batch.commit();

                toast.success("Event successfully archived", {
                    autoClose: 2000,
                });

                archiveDialog.value = false;
            } else {
                toast.error("Unable to save details");
            }

            loading.value = false;
        };

        const confirmDelete = () => {
            deleteDialog.value = true;
        };

        const deleteEvent = async () => {
            loading.value = true;

            const storage = getStorage();

            await deleteDoc(doc(db, "Events", props.event.id));

            await deleteObject(storageRef(storage, props.event.ThumbnailUrl))
                .then(() => {
                    toast.success("Event successfully deleted", {
                        autoClose: 2000,
                    });
                })
                .catch((error) => {
                    console.log(error);
                    toast.error("Unable to delete event photo");
                });

            // Get all event registrations and volunteers for this event
            const eventRegistrationsCollection = collection(
                db,
                "Event Registrations"
            );
            const volunteersCollection = collection(db, "Volunteer Applications");
            const eventsQuery = query(
                eventRegistrationsCollection,
                where("EventRegisteredId", "==", props.event.id),
                where("IsArchived", "==", false)
            );
            const volunteersQuery = query(
                volunteersCollection,
                where("EventAppliedId", "==", props.event.id),
                where("IsArchived", "==", false)
            );

            const eventsQuerySnapshot = await getDocs(eventsQuery);
            const volunteersQuerySnapshot = await getDocs(volunteersQuery);

            const batch = writeBatch(db);


            // Archive all event registrations and volunteers associated with this event
            eventsQuerySnapshot.forEach((doc) => {
                batch.update(doc.ref, {
                    IsArchived: true,
                });
            });

            volunteersQuerySnapshot.forEach((doc) => {
                batch.update(doc.ref, {
                    IsArchived: true,
                });
            });

            await batch.commit();

            loading.value = false;
        };

        return {
            updateFormData,
            archiveFormData,
            updateForm$,
            archiveForm$,
            resetUpdateForm,
            resetArchiveForm,
            submitUpdateFormData,
            submitArchiveFormData,
            deleteEvent,
            editDialog,
            archiveDialog,
            deleteDialog,
            confirmDelete,
            onFileChange,
            deleteFile,
            loading,
        };
    },
    methods: {
        async setupImage() {
            this.uploadedEventPhoto = this.eventPhoto;
            this.uploadedEventPhotoUrl = this.event.ThumbnailUrl;
            this.editDialog = true;
        },
        previewPhotoUrl(event) {
            this.uploadedEventPhoto = event.target.files[0];
            this.uploadedEventPhotoUrl = URL.createObjectURL(event.target.files[0]);
        },
        deleteEventPhotoUrl() {
            this.uploadedEventPhoto = null;
            this.uploadedEventPhotoUrl = null;
        },
        loadVolunteers() {
            const volunteers = ref([]);

            const loadData = async () => {
                const q = query(
                    collection(db, "Volunteer Applications"),
                    where("EventAppliedId", "==", this.event.id),
                    where("IsAccepted", "==", true),
                    where("IsArchived", "==", false),
                    orderBy("CreationTimestamp", "asc")
                );
                const querySnapshot = await getDocs(q);
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    volunteers.value.push({ id: doc.id, ...data });
                });
            };

            loadData();
            return { volunteers };
        },
        changeVisiblity(event) {
            // If event has a host, set eventIsOpen to true
            if (event.target.value == ["", ""]) {
                this.updateFormData.eventIsOpen = false;
            }
        },
    },
    computed: {
        user() {
            return store.getters["auth/user"];
        },
        emptyImageUrl() {
            return store.getters["events/emptyImageUrl"];
        },
        eventVolunteers() {
            return this.loadVolunteers(this.event.id).volunteers.value;
        },
        eventPhoto() {
            const storage = getStorage();
            return storageRef(storage, this.event.ThumbnailUrl);
        },
    },
};
</script>

<style scoped src="@/assets/css/admin.css"></style>
